import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingCart, Truck, ArrowLeft, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { createOrder, fmtPrice } from "@/lib/api";
import { fbTrack } from "@/lib/pixel";
import { JUDETE } from "@/lib/judete";

const FIELDS = [
  { name: "name", label: "Nume complet", placeholder: "Ion Popescu", type: "text" },
  { name: "phone", label: "Telefon", placeholder: "07xx xxx xxx", type: "tel" },
  { name: "address", label: "Adresă (stradă, nr.)", placeholder: "Str. Florilor nr. 10", type: "text" },
  { name: "city", label: "Oraș / Localitate", placeholder: "București", type: "text" },
  { name: "county", label: "Județ", placeholder: "Alege județul", type: "select" },
];


export default function Checkout() {
  const { items, subtotal, shipping, total, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", address: "", city: "", county: "", notes: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Declanșează InitiateCheckout cu valoarea reală a coșului (o singură dată).
  useEffect(() => {
    if (items.length > 0) {
      fbTrack("InitiateCheckout", {
        value: total,
        currency: "RON",
        contents: items.map((i) => ({ id: i.product_id, quantity: i.quantity })),
        num_items: items.reduce((s, i) => s + i.quantity, 0),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validate = () => {
    const err = {};
    FIELDS.forEach((f) => {
      if (!form[f.name].trim()) err[f.name] = "Câmp obligatoriu";
    });
    if (form.phone && form.phone.replace(/\D/g, "").length < 10) err.phone = "Telefon invalid";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Coșul este gol");
      return;
    }
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        customer: form,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
        })),
      };
      const order = await createOrder(payload);
      clear();
      navigate("/comanda-plasata", { state: { order } });
    } catch (err) {
      toast.error("A apărut o eroare. Încearcă din nou.");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="checkout-empty">
        <ShoppingCart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h1 className="font-display font-black text-navy text-2xl">Coșul tău este gol</h1>
        <p className="text-muted-foreground mt-2">Adaugă produse înainte de a finaliza comanda.</p>
        <Link to="/produse" className="inline-block mt-6 bg-eco hover:bg-green-700 text-white font-bold px-6 py-3 rounded-xl transition-colors">
          Vezi produsele
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-[70vh]" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/produse" className="inline-flex items-center gap-2 text-sm font-bold text-navy hover:text-eco mb-6">
          <ArrowLeft className="h-4 w-4" /> Înapoi la produse
        </Link>
        <h1 className="font-display font-black text-navy text-3xl md:text-4xl tracking-tighter mb-8">FINALIZEAZĂ COMANDA</h1>

        <form onSubmit={submit} className="grid lg:grid-cols-5 gap-8" data-testid="checkout-form">
          {/* Form */}
          <div className="lg:col-span-3 bg-white rounded-2xl border-2 border-slate-100 p-6 sm:p-8">
            <h2 className="font-display font-bold text-navy text-lg mb-5">Date de livrare</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.name === "address" ? "sm:col-span-2" : ""}>
                  <label className="block text-sm font-bold text-navy mb-1.5">{f.label}</label>
                  {f.type === "select" ? (
                    <select
                      name={f.name}
                      value={form[f.name]}
                      onChange={change}
                      className={`w-full rounded-xl border-2 px-4 py-2.5 text-sm outline-none transition-colors focus:border-eco bg-white ${
                        errors[f.name] ? "border-red-400" : "border-slate-200"
                      } ${form[f.name] ? "text-navy" : "text-slate-400"}`}
                      data-testid={`checkout-input-${f.name}`}
                    >
                      <option value="" disabled>{f.placeholder}</option>
                      {JUDETE.map((j) => (
                        <option key={j} value={j} className="text-navy">{j}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      name={f.name}
                      type={f.type}
                      value={form[f.name]}
                      onChange={change}
                      placeholder={f.placeholder}
                      className={`w-full rounded-xl border-2 px-4 py-2.5 text-sm outline-none transition-colors focus:border-eco ${
                        errors[f.name] ? "border-red-400" : "border-slate-200"
                      }`}
                      data-testid={`checkout-input-${f.name}`}
                    />
                  )}
                  {errors[f.name] && <p className="text-xs text-red-500 mt-1">{errors[f.name]}</p>}
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="block text-sm font-bold text-navy mb-1.5">Observații (opțional)</label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={change}
                  rows={3}
                  placeholder="Detalii pentru livrare..."
                  className="w-full rounded-xl border-2 border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-eco"
                  data-testid="checkout-input-notes"
                />
              </div>
            </div>

            <div className="mt-5 flex items-center gap-3 bg-eco/10 rounded-xl px-4 py-3">
              <Truck className="h-5 w-5 text-eco" />
              <div>
                <p className="font-bold text-navy text-sm">Plata la livrare (ramburs)</p>
                <p className="text-xs text-muted-foreground">Plătești curierului la primirea coletului.</p>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 sticky top-24">
              <h2 className="font-display font-bold text-navy text-lg mb-4">Sumar comandă</h2>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {items.map((i) => (
                  <div key={i.product_id} className="flex gap-3 items-center">
                    <img src={i.image} alt={i.name} className="h-14 w-14 object-contain bg-slate-50 rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-navy text-sm leading-tight">{i.name}</p>
                      <p className="text-xs text-muted-foreground">{i.quantity} × {fmtPrice(i.price)} lei</p>
                    </div>
                    <span className="font-bold text-navy text-sm">{fmtPrice(i.price * i.quantity)} lei</span>
                  </div>
                ))}
              </div>
              <div className="border-t mt-4 pt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-bold">{fmtPrice(subtotal)} lei</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Transport</span><span className="font-bold">{shipping === 0 ? "Gratuit" : `${fmtPrice(shipping)} lei`}</span></div>
                <div className="flex justify-between font-display font-black text-navy text-lg pt-2 border-t">
                  <span>Total</span><span data-testid="checkout-total">{fmtPrice(total)} lei</span>
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full mt-5 bg-eco hover:bg-green-700 disabled:opacity-60 text-white font-display font-black py-3.5 rounded-xl flex items-center justify-center gap-2 transition-colors"
                data-testid="submit-order-button"
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShoppingCart className="h-5 w-5" />}
                {submitting ? "SE TRIMITE..." : "PLASEAZĂ COMANDA"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
